import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Settings, Building2, FileText, Zap, Save } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const SystemParameters = ({ user }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({
    sistema_nombre: 'LICORSTORE',
    mensaje_bienvenida: 'LICORSTORE RD',
    slogan: 'Sistema de Gestión de Licorería',
    nombre_comercio: '',
    rnc: '',
    telefono: '',
    direccion: '',
    email: '',
    ciudad: '',
    provincia: '',
    logo: '',
    ncf_habilitado: true,
    itbis_habilitado: true,
    itbis_porcentaje: 18.0,
    allow_open_invoices: true,
    allow_edit_stock_cost: true,
    facturas_pendientes_cobro_habilitado: false,
    error_notifications_enabled: false,
    error_notification_email: '',
    smtp_host: 'smtp.gmail.com',
    smtp_port: 587,
    smtp_user: '',
    smtp_password: '',
    smtp_use_tls: true
  });

  // Verificar que el usuario sea superadmin
  useEffect(() => {
    // Solo verificar si el usuario ya ha sido cargado
    if (user && user.role !== 'superadmin') {
      toast.error('Acceso denegado. Solo SuperAdmin puede acceder a esta sección.');
      navigate('/');
    }
  }, [user, navigate]);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API}/business-config`);
      setConfig({
        sistema_nombre: response.data.sistema_nombre || 'LICORSTORE',
        mensaje_bienvenida: response.data.mensaje_bienvenida || 'LICORSTORE RD',
        slogan: response.data.slogan || 'Sistema de Gestión de Licorería',
        nombre_comercio: response.data.nombre_comercio || '',
        rnc: response.data.rnc || '',
        telefono: response.data.telefono || '',
        direccion: response.data.direccion || '',
        email: response.data.email || '',
        ciudad: response.data.ciudad || '',
        provincia: response.data.provincia || '',
        logo: response.data.logo || '',
        ncf_habilitado: response.data.ncf_habilitado ?? true,
        itbis_habilitado: response.data.itbis_habilitado ?? true,
        itbis_porcentaje: response.data.itbis_porcentaje || 18.0,
        allow_open_invoices: response.data.allow_open_invoices ?? true,
        allow_edit_stock_cost: response.data.allow_edit_stock_cost ?? true,
        facturas_pendientes_cobro_habilitado: response.data.facturas_pendientes_cobro_habilitado ?? false,
        error_notifications_enabled: response.data.error_notifications_enabled ?? false,
        error_notification_email: response.data.error_notification_email || '',
        smtp_host: response.data.smtp_host || 'smtp.gmail.com',
        smtp_port: response.data.smtp_port || 587,
        smtp_user: response.data.smtp_user || '',
        smtp_password: response.data.smtp_password || '',
        smtp_use_tls: response.data.smtp_use_tls ?? true
      });
    } catch (error) {
      toast.error('Error al cargar la configuración');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!config.nombre_comercio.trim() || !config.rnc.trim()) {
      toast.error('Nombre del comercio y RNC son obligatorios');
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API}/business-config`, config);
      toast.success('Configuración guardada exitosamente');
      fetchConfig();
    } catch (error) {
      toast.error('Error al guardar la configuración');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold flex items-center gap-3">
          <Settings className="text-emerald-600" size={36} />
          Parámetros del Sistema
        </h1>
        <p className="text-slate-600 mt-2">Configuración avanzada del sistema (Solo SuperAdmin)</p>
      </div>

      <div className="space-y-6">
        {/* Información del Sistema */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <Zap className="text-blue-600" size={24} />
            Información del Sistema
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="sistema_nombre">Nombre del Sistema</Label>
              <Input
                id="sistema_nombre"
                value={config.sistema_nombre}
                onChange={(e) => setConfig({ ...config, sistema_nombre: e.target.value })}
                placeholder="LICORSTORE"
              />
              <p className="text-xs text-slate-500 mt-1">Se muestra en el menú lateral</p>
            </div>
            <div>
              <Label htmlFor="mensaje_bienvenida">Mensaje de Bienvenida</Label>
              <Input
                id="mensaje_bienvenida"
                value={config.mensaje_bienvenida}
                onChange={(e) => setConfig({ ...config, mensaje_bienvenida: e.target.value })}
                placeholder="LICORSTORE RD"
              />
              <p className="text-xs text-slate-500 mt-1">Se muestra en la pantalla de login</p>
            </div>
            <div>
              <Label htmlFor="slogan">Slogan</Label>
              <Input
                id="slogan"
                value={config.slogan}
                onChange={(e) => setConfig({ ...config, slogan: e.target.value })}
                placeholder="Sistema de Gestión de Licorería"
              />
              <p className="text-xs text-slate-500 mt-1">Se muestra debajo del nombre del sistema</p>
            </div>
          </div>
        </div>

        {/* Datos del Negocio */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <Building2 className="text-emerald-600" size={24} />
            Datos del Negocio
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="nombre_comercio">Nombre Comercial *</Label>
              <Input
                id="nombre_comercio"
                value={config.nombre_comercio}
                onChange={(e) => setConfig({ ...config, nombre_comercio: e.target.value })}
                placeholder="Nombre del negocio"
                required
              />
            </div>
            <div>
              <Label htmlFor="rnc">RNC *</Label>
              <Input
                id="rnc"
                value={config.rnc}
                onChange={(e) => setConfig({ ...config, rnc: e.target.value })}
                placeholder="000-00000000-0"
                required
              />
            </div>
            <div>
              <Label htmlFor="telefono">Teléfono</Label>
              <Input
                id="telefono"
                value={config.telefono}
                onChange={(e) => setConfig({ ...config, telefono: e.target.value })}
                placeholder="809-000-0000"
              />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={config.email}
                onChange={(e) => setConfig({ ...config, email: e.target.value })}
                placeholder="email@ejemplo.com"
              />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="direccion">Dirección</Label>
              <Input
                id="direccion"
                value={config.direccion}
                onChange={(e) => setConfig({ ...config, direccion: e.target.value })}
                placeholder="Dirección completa"
              />
            </div>
            <div>
              <Label htmlFor="ciudad">Ciudad</Label>
              <Input
                id="ciudad"
                value={config.ciudad}
                onChange={(e) => setConfig({ ...config, ciudad: e.target.value })}
                placeholder="Ciudad"
              />
            </div>
            <div>
              <Label htmlFor="provincia">Provincia</Label>
              <Input
                id="provincia"
                value={config.provincia}
                onChange={(e) => setConfig({ ...config, provincia: e.target.value })}
                placeholder="Provincia"
              />
            </div>
          </div>
        </div>

        {/* Configuración Fiscal */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <FileText className="text-purple-600" size={24} />
            Configuración Fiscal
          </h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <h3 className="font-semibold">Comprobantes Fiscales (NCF)</h3>
                <p className="text-sm text-slate-600">Activar emisión de NCF en facturas</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.ncf_habilitado}
                  onChange={(e) => setConfig({ ...config, ncf_habilitado: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
              </label>
            </div>

            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <h3 className="font-semibold">ITBIS (Impuesto)</h3>
                <p className="text-sm text-slate-600">Activar cobro de ITBIS en ventas</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.itbis_habilitado}
                  onChange={(e) => setConfig({ ...config, itbis_habilitado: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
              </label>
            </div>

            {config.itbis_habilitado && (
              <div className="pl-4">
                <Label htmlFor="itbis_porcentaje">Porcentaje de ITBIS (%)</Label>
                <Input
                  id="itbis_porcentaje"
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={config.itbis_porcentaje}
                  onChange={(e) => setConfig({ ...config, itbis_porcentaje: parseFloat(e.target.value) })}
                  className="w-40"
                />
              </div>
            )}
          </div>
        </div>

        {/* Funcionalidades del Sistema */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <Settings className="text-orange-600" size={24} />
            Funcionalidades del Sistema
          </h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <h3 className="font-semibold">Facturas Abiertas (Cuentas Pendientes)</h3>
                <p className="text-sm text-slate-600">Permitir guardar cuentas pendientes en el POS</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.allow_open_invoices}
                  onChange={(e) => setConfig({ ...config, allow_open_invoices: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
              </label>
            </div>

            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <h3 className="font-semibold">Modificar Existencia y Costo desde Productos</h3>
                <p className="text-sm text-slate-600">Permitir editar stock y costo de compra directamente en el módulo de productos</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.allow_edit_stock_cost}
                  onChange={(e) => setConfig({ ...config, allow_edit_stock_cost: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>

            <div className="flex items-center justify-between p-4 bg-amber-50 rounded-lg border border-amber-200" data-testid="toggle-facturas-pendientes-cobro-row">
              <div>
                <h3 className="font-semibold text-amber-900">Facturas Pendientes de Cobro en Caja</h3>
                <p className="text-sm text-amber-800">
                  Si se activa, las facturas creadas desde el módulo de <strong>Facturación</strong> quedarán en estado
                  <span className="font-semibold"> &quot;Pendiente de cobro&quot;</span> y deberán completarse
                  (método de pago, confirmación e impresión) desde la pantalla de <strong>Caja</strong>.
                  El stock se reserva al crear la factura; el NCF se genera solo al cobrar.
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer ml-4">
                <input
                  type="checkbox"
                  checked={config.facturas_pendientes_cobro_habilitado}
                  onChange={(e) => setConfig({ ...config, facturas_pendientes_cobro_habilitado: e.target.checked })}
                  className="sr-only peer"
                  data-testid="toggle-facturas-pendientes-cobro-input"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-amber-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-600"></div>
              </label>
            </div>
          </div>
        </div>

        {/* Notificaciones de Errores por Email */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <Settings className="text-red-600" size={24} />
            Notificaciones de Errores por Email
          </h2>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <h3 className="font-semibold">Enviar emails cuando ocurran errores</h3>
                <p className="text-sm text-slate-600">Recibe notificaciones automáticas de errores del sistema</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.error_notifications_enabled}
                  onChange={(e) => setConfig({ ...config, error_notifications_enabled: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
              </label>
            </div>

            {config.error_notifications_enabled && (
              <div className="space-y-4 pl-4 border-l-4 border-red-300">
                <div>
                  <Label htmlFor="error_notification_email">Email para Notificaciones *</Label>
                  <Input
                    id="error_notification_email"
                    type="email"
                    value={config.error_notification_email}
                    onChange={(e) => setConfig({ ...config, error_notification_email: e.target.value })}
                    placeholder="admin@tuempresa.com"
                  />
                  <p className="text-xs text-slate-500 mt-1">Email donde se enviarán las notificaciones de errores</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="smtp_host">Servidor SMTP *</Label>
                    <Input
                      id="smtp_host"
                      value={config.smtp_host}
                      onChange={(e) => setConfig({ ...config, smtp_host: e.target.value })}
                      placeholder="smtp.gmail.com"
                    />
                  </div>
                  <div>
                    <Label htmlFor="smtp_port">Puerto SMTP *</Label>
                    <Input
                      id="smtp_port"
                      type="number"
                      value={config.smtp_port}
                      onChange={(e) => setConfig({ ...config, smtp_port: parseInt(e.target.value) })}
                      placeholder="587"
                      className="w-32"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="smtp_user">Usuario SMTP (Email) *</Label>
                  <Input
                    id="smtp_user"
                    type="email"
                    value={config.smtp_user}
                    onChange={(e) => setConfig({ ...config, smtp_user: e.target.value })}
                    placeholder="tu-email@gmail.com"
                  />
                </div>

                <div>
                  <Label htmlFor="smtp_password">Contraseña SMTP *</Label>
                  <Input
                    id="smtp_password"
                    type="password"
                    value={config.smtp_password}
                    onChange={(e) => setConfig({ ...config, smtp_password: e.target.value })}
                    placeholder="••••••••••••••••"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Para Gmail, usa una "Contraseña de aplicación" en lugar de tu contraseña normal
                  </p>
                </div>

                <div className="flex items-center gap-2 p-3 bg-blue-50 rounded">
                  <input
                    type="checkbox"
                    id="smtp_use_tls"
                    checked={config.smtp_use_tls}
                    onChange={(e) => setConfig({ ...config, smtp_use_tls: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="smtp_use_tls" className="cursor-pointer">
                    Usar TLS/SSL (Recomendado)
                  </Label>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <h4 className="font-semibold text-amber-800 mb-2">📧 Configuración para Gmail:</h4>
                  <ul className="text-sm text-amber-700 space-y-1">
                    <li>• Servidor: smtp.gmail.com</li>
                    <li>• Puerto: 587</li>
                    <li>• Crear "Contraseña de aplicación" en tu cuenta Google</li>
                    <li>• <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer" className="underline">Crear contraseña de aplicación →</a></li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Información Técnica */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <Settings className="text-slate-600" size={24} />
            Información Técnica
          </h2>
          <div className="bg-slate-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm font-medium text-slate-600">URL del API (Backend)</Label>
                <div className="mt-1 flex items-center gap-2">
                  <code className="bg-slate-200 px-3 py-2 rounded text-sm font-mono text-slate-800">
                    {API}
                  </code>
                  <span className="text-xs text-slate-500">(Solo lectura)</span>
                </div>
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-3">
              Esta es la URL que utiliza el sistema para comunicarse con el servidor backend.
            </p>
          </div>
        </div>

        {/* Botón Guardar */}
        <div className="flex justify-end gap-3">
          <Button
            variant="outline"
            onClick={() => navigate('/dashboard')}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            <Save className="mr-2" size={20} />
            {saving ? 'Guardando...' : 'Guardar Configuración'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SystemParameters;
